import { useEffect, useRef, useState } from 'react';

/**
 * useDebouncedSave — Notion/Linear-style auto-save hook.
 *
 * Watches a value and, after `delay` ms of stillness, calls `save(value)`.
 * Exposes a `status` ("idle" | "saving" | "saved" | "error") that drives
 * the "Saved ✓" pill shown next to forms across the app.
 *
 * Design notes:
 *  • Skips the FIRST run after mount — otherwise we'd PATCH on initial
 *    load with whatever stub value the form was hydrated with, generating
 *    needless backend writes.
 *  • `enabled` lets the parent gate auto-save (e.g. "don't save until
 *    initial fetch resolved"). Defaults to true.
 *  • Status auto-clears back to "idle" 1500 ms after "saved" — drives the
 *    ephemeral pill UX.
 *  • Caller controls the actual save function so this stays HTTP-library
 *    agnostic (works with axios, fetch, the AuthContext `api`, etc).
 *
 * Usage:
 *   const { status, error } = useDebouncedSave(form, async (v) => {
 *     await api.put(`/restaurants/${id}`, v);
 *   });
 */
export default function useDebouncedSave(value, save, {
  delay = 700,
  enabled = true,
  // Skip the next change exactly N times — used to swallow the burst of
  // sets that happen right after the initial fetch hydrates the form.
  skipNext = 1,
} = {}) {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const saveTimer = useRef(null);
  const clearTimer = useRef(null);
  const skipCount = useRef(skipNext);
  const lastSerialized = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const serialized = JSON.stringify(value);

    // First N changes after mount/hydration are skipped — see comment above.
    if (skipCount.current > 0) {
      skipCount.current -= 1;
      lastSerialized.current = serialized;
      return undefined;
    }

    // Bail early on a no-op (React StrictMode double-fires, or a parent
    // re-renders with the same form object — don't burn an API call).
    if (serialized === lastSerialized.current) return undefined;
    lastSerialized.current = serialized;

    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setStatus('saving');
      setError(null);
      try {
        await save(value);
        setStatus('saved');
        if (clearTimer.current) clearTimeout(clearTimer.current);
        clearTimer.current = setTimeout(() => setStatus('idle'), 1500);
      } catch (e) {
        setStatus('error');
        setError(e?.response?.data?.detail || e?.message || 'Save failed');
      }
    }, delay);

    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value), enabled]);

  // Flush timers on unmount.
  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (clearTimer.current) clearTimeout(clearTimer.current);
  }, []);

  return { status, error };
}
